import EngineeringIcon from "@mui/icons-material/Engineering";
import StorageIcon from "@mui/icons-material/Storage";
import FeedIcon from "@mui/icons-material/Feed";
import PeopleIcon from "@mui/icons-material/People";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";

import { isAdmin } from "../../helper-functions/shared/miscUtils";

import { getLanguage } from "../language/language";

const LANG = getLanguage();

// // // // //
// GENERAL
// Misc. constants
// // // // //
export const PAGE_VERSION = "v0.3.0";
export const INDEX_PATH = ""; // Web server's index.html path.
export const PAGE_ENABLE_LOADER = true; // Show spinner animation.
export const PAGE_LOADING_MS = 1200; // Page spinner.

export const IFRAME_TIMEOUT_MS = 7500; // Show an error message if the iframe is not loaded after this time.
export const HTTP_QUERY_TIMEOUT_MS = 60000; // Timeout for HTTP queries.

export const FRONTEND_ONLY = false; // Used in changing the API paths to static local JSON files.
export const ALLOW_EXPERIMENTAL_FEATURES = true; // Allow enabling experimental features in the UI.

// NB! These are invalid tokens, but fit our debug purposes well.
export const DEBUG_ADMIN_JWT =
    "eyJhbGciOiJIUzUxMiJ9.eyJyb2xlIjoiUk9MRV9BRE1JTiIsInVzZXJuYW1lIjoiYWRtaW4ifQ.NxCPtUwVPmayPbiokxxUwjCuNZt--bwK8dAz96-7UeoNPUIdIm6Evk36rfypnOLyh-B_9pS7eWRgHR_sLHS0lA";
export const DEBUG_USER_JWT =
    "eyJhbGciOiJIUzUxMiJ9.eyJyb2xlIjoiUk9MRV9VU0VSIiwidXNlcm5hbWUiOiJ1c2VyIn0.D3K0mZDOnkFc0wvNRgklUyVulJTjdM0af-Y0dT1dp6W3QtrnrX3lCEur_Rm0XxiaVboUAGNF68IQ8fKhAPi9tw";

// // // // //
// PAGE ELEMENTS
// Toolbar, drawer, and other HTML elements.
// Also includes pagination.
// // // // //
export const AUTO_OPEN_SIDEBAR_PX = 1200;
export const HIDE_TOOLBAR_DESCRIPTION_PX = 1015;
export const PAGE_DRAWER_WIDTH_PX = 275;
export const ELEMENT_MIN_WIDTH_PX = 285;
export const SIDEBAR_TEXT_MIN_WIDTH = 24;

export const TABLE_ROWS_PER_PAGE = [5, 10, 25, 50];
export const DEFAULT_TABLE_ROWS_PER_PAGE = 10;

export const TABLE_LOADING_MS = 2500; // Show loading widget before deciding whether to show the table.
export const TABLE_HEIGHT_DESKTOP_PX = 420;
export const TABLE_HEIGHT_MOBILE_PX = 325;
export const TABLE_ROW_HEIGHT_DESKTOP_PX = 50;
export const TABLE_ROW_HEIGHT_MOBILE_PX = 30;

// // // // //
// SERVER
// Network parameters
// // // // //
export const DEVELOPMENT_ENV = ["localhost", "127.0.0.1"].includes(window.location.hostname) && window.location.port === "3000";

const DEFAULT_SERVER_IP = window.location.protocol + "//" + window.location.hostname; // E.g. http://193.40.255.27
const DEFAULT_SERVER_PORT = DEVELOPMENT_ENV ? ":8080" : "";

// Runtime overrides: if `public/env-config.js` (served statically) defines `window._env_`, prefer those values.
// This file can be edited or mounted in production without rebuilding the frontend bundle.
const RUNTIME_ENV = typeof window !== "undefined" && window._env_ ? window._env_ : {};

export const SERVER_IP = DEVELOPMENT_ENV ? DEFAULT_SERVER_IP : RUNTIME_ENV.SERVER_IP || DEFAULT_SERVER_IP;
export const SERVER_PORT = DEVELOPMENT_ENV ? DEFAULT_SERVER_PORT : RUNTIME_ENV.SERVER_PORT || DEFAULT_SERVER_PORT;

// // // // //
// API PATHS
// API endpoints to query data from the API.
// // // // //
export const API_JWT_VALIDATION = "/api/validate";
export const API_LOGIN_PATH = "/api/signin";
export const API_MFA_ENROLL_PATH = "/api/mfa/enroll";
export const API_MFA_VERIFY_PATH = "/api/mfa/verify";
export const API_MFA_BACKUP_CODES_PATH = "/api/mfa/backup-codes";
export const API_SIGNUP_PATH = "/api/signup";

export const API_PROGRAM_STATUS = "/api/status";

export const API_USERS = "/api/users";
export const API_USERS_ID = "/api/users/<user_id>";
export const API_USERS_ME = "/api/users/me";

export const API_EXAMINATIONS = "/api/examinations";
export const API_EXAMINATIONS_ID = "/api/examinations/<examination_id>";
export const API_EXAMINATIONS_ID_PATIENT = "/api/examinations/<examination_id>/patient";
export const API_EXAMINATIONS_ID_APPROVE = "/api/examinations/<examination_id>/approve";
export const API_EXAMINATIONS_ID_DISAPPROVE = "/api/examinations/<examination_id>/disapprove";
export const API_EXAMINATIONS_APPROVE = "/api/examinations/approve";
export const API_EXAMINATIONS_DISAPPROVE = "/api/examinations/disapprove";

export const API_PATIENTS = "/api/patients";
export const API_PATIENTS_MATCH = "/api/patients/match";
export const API_PATIENTS_SIMILAR = "/api/patients/similar";
export const API_PATIENTS_ID = "/api/patients/<patient_id>";
export const API_PATIENTS_ID_EXAMINATIONS = "/api/patients/<patient_id>/examinations";
export const API_PATIENTS_ID_APPROVE = "/api/patients/<patient_id>/approve";
export const API_PATIENTS_ID_DISAPPROVE = "/api/patients/<patient_id>/disapprove";
export const API_PATIENTS_APPROVE = "/api/patients/approve";
export const API_PATIENTS_DISAPPROVE = "/api/patients/disapprove";

export const API_STATISTICS_EXAMINATIONS = "/api/statistics/examinations";
export const API_STATISTICS_PATIENTS = "/api/statistics/patients";

// // // // //
// AUTH AND PRIVILEGES
export const ROLE_USER = "USER";
export const ROLE_ADMIN = "ADMIN";
export const ROLE_SYSTEM = "SYSTEM";

// // // // //
// OBJECT STRUCTURES
// Define the basic structure of objects.
// NB! All configuration 'form' elements are automatically added during parsing.
// // // // //

// The structure of a configuration form field, describing whether it has any errors.
export const FORM_ELEMENT = {
    error: false,
    helperText: ""
};

export const USER_ENABLED_TYPES = [true, false];

export const ROLE_TYPES = [
    { id: 1, role: ROLE_SYSTEM },
    { id: 2, role: ROLE_USER },
    { id: 3, role: ROLE_ADMIN }
];

export const USER_REQUEST_OBJECT = {
    username: "",
    email: "",
    role_id: "",
    status: "",

    // Temporary fields.
    _role: ""
};

// Admin-created user. Unlike the edit form this also carries an initial password.
export const USER_CREATE_REQUEST_OBJECT = {
    username: "",
    email: "",
    password: "",
    role_id: 2,
    enabled: true,

    // Temporary fields.
    _role: ROLE_USER
};

// Patient object template for forms and requests
export const PATIENT_REQUEST_OBJECT = {
    patient_identifier: "",
    first_name: "",
    last_name: "",
    birth_date: "",
    notes: ""
};

export const PATIENT_NULLABLE_FIELDS = ["patient_identifier", "first_name", "last_name", "birth_date", "notes"];

// Examination obejcts below

// FDI: Deciduous (primary) and permanent teeth, by quadrant
export const DECIDIOUS_UPPER_RIGHT = [55, 54, 53, 52, 51];
export const DECIDIOUS_UPPER_LEFT = [61, 62, 63, 64, 65];
export const DECIDIOUS_LOWER_LEFT = [71, 72, 73, 74, 75];
export const DECIDIOUS_LOWER_RIGHT = [85, 84, 83, 82, 81];

export const PERM_UPPER_RIGHT = [18, 17, 16, 15, 14, 13, 12, 11];
export const PERM_UPPER_LEFT = [21, 22, 23, 24, 25, 26, 27, 28];
export const PERM_LOWER_LEFT = [31, 32, 33, 34, 35, 36, 37, 38];
export const PERM_LOWER_RIGHT = [48, 47, 46, 45, 44, 43, 42, 41];

export const ARCH_ROWS = [
    [DECIDIOUS_UPPER_RIGHT, DECIDIOUS_UPPER_LEFT],
    [PERM_UPPER_RIGHT, PERM_UPPER_LEFT],
    [PERM_LOWER_RIGHT, PERM_LOWER_LEFT],
    [DECIDIOUS_LOWER_RIGHT, DECIDIOUS_LOWER_LEFT]
];

export const PERMANENT_ARCH_ROWS = [
    [PERM_UPPER_RIGHT, PERM_UPPER_LEFT],
    [PERM_LOWER_RIGHT, PERM_LOWER_LEFT]
];

// Mapping between deciduous (milk) teeth and their permanent replacements
// When a deciduous tooth remains, it takes the position of the permanent tooth
export const DECIDUOUS_TO_PERMANENT = {
    // Upper right: 55→15, 54→14, 53→13, 52→12, 51→11
    55: 15,
    54: 14,
    53: 13,
    52: 12,
    51: 11,
    // Upper left: 61→21, 62→22, 63→23, 64→24, 65→25
    61: 21,
    62: 22,
    63: 23,
    64: 24,
    65: 25,
    // Lower left: 71→31, 72→32, 73→33, 74→34, 75→35
    71: 31,
    72: 32,
    73: 33,
    74: 34,
    75: 35,
    // Lower right: 81→41, 82→42, 83→43, 84→44, 85→45
    81: 41,
    82: 42,
    83: 43,
    84: 44,
    85: 45
};

// Reverse mapping: permanent to deciduous
export const PERMANENT_TO_DECIDUOUS = Object.fromEntries(Object.entries(DECIDUOUS_TO_PERMANENT).map(([dec, perm]) => [perm, Number(dec)]));

// probing_depth: 1..10 mm
// recession: 0..10 mm
// furcation: 1..3
// mobility: 1..3
const TOOTH_TEMPLATE = {
    missing: false,
    implant: false,
    db: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    b: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    mb: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    dp: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    p: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    mp: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    dl: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    l: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    ml: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    mobility: null
};

const DECIDUOUS_TOOTH_TEMPLATE = {
    missing: true, // Deciduous teeth missing by default
    implant: false,
    db: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    b: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    mb: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    dp: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    p: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    mp: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    dl: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    l: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    ml: { bleeding: false, probing_depth: null, recession: null, sup: false, plaque: false, calculus: false, erythema: false, furcation: null },
    mobility: null
};

export const CHARTING_TABLE_OBJECT = {
    // Deciduous teeth (upper right)
    55: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    54: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    53: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    52: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    51: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),

    // Deciduous teeth (upper left)
    61: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    62: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    63: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    64: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    65: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),

    // Deciduous teeth (lower left)
    71: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    72: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    73: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    74: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    75: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),

    // Deciduous teeth (lower right)
    85: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    84: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    83: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    82: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),
    81: JSON.parse(JSON.stringify(DECIDUOUS_TOOTH_TEMPLATE)),

    // Upper right quadrant (18-11)
    18: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    17: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    16: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    15: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    14: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    13: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    12: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    11: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),

    // Upper left quadrant (21-28)
    21: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    22: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    23: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    24: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    25: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    26: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    27: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    28: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),

    // Lower right quadrant (48-41)
    48: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    47: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    46: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    45: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    44: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    43: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    42: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    41: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),

    // Lower left quadrant (31-38)
    31: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    32: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    33: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    34: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    35: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    36: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    37: JSON.parse(JSON.stringify(TOOTH_TEMPLATE)),
    38: JSON.parse(JSON.stringify(TOOTH_TEMPLATE))
};

// Legacy objects for backward compatibility - now reference CHARTING_TABLE_OBJECT
export const MISSING_TEETH_OBJECT = Object.fromEntries(Object.keys(CHARTING_TABLE_OBJECT).map((tooth) => [tooth, CHARTING_TABLE_OBJECT[tooth].missing]));

export const IMPLANTS_OBJECT = Object.fromEntries(Object.keys(CHARTING_TABLE_OBJECT).map((tooth) => [tooth, CHARTING_TABLE_OBJECT[tooth].implant]));

export const PLAQUE_OBJECT = Object.fromEntries(
    Object.keys(CHARTING_TABLE_OBJECT).map((tooth) => [
        tooth,
        {
            db: CHARTING_TABLE_OBJECT[tooth].db.plaque,
            b: CHARTING_TABLE_OBJECT[tooth].b.plaque,
            mb: CHARTING_TABLE_OBJECT[tooth].mb.plaque,
            dp: CHARTING_TABLE_OBJECT[tooth].dp.plaque,
            p: CHARTING_TABLE_OBJECT[tooth].p.plaque,
            mp: CHARTING_TABLE_OBJECT[tooth].mp.plaque,
            dl: CHARTING_TABLE_OBJECT[tooth].dl.plaque,
            l: CHARTING_TABLE_OBJECT[tooth].l.plaque,
            ml: CHARTING_TABLE_OBJECT[tooth].ml.plaque
        }
    ])
);

// // // // //
// TOUR STEPS
// Used in the tour guides, describing the app's features.
// // // // //
const tourStep = (target, content, path, placement) => ({
    target,
    content: <span style={{ whiteSpace: "pre-line" }}>{content}</span>,
    placement,
    data: { path }
});

export const TOUR_GUIDE_STEPS = {
    ABOUT: {
        paths: ["/about", "/database/examinations", "/database/patients"],
        steps: [
            tourStep("body", LANG.TOUR_ABOUT_1, "/about", "center"),
            tourStep(".tour-about-2", LANG.TOUR_ABOUT_2, "/about"),
            tourStep(".tour-about-3", LANG.TOUR_ABOUT_3, "/about"),
            tourStep(".tour-about-4", LANG.TOUR_ABOUT_4, "/about"),
            tourStep("body", LANG.TOUR_ABOUT_5, "/about", "center"),
            tourStep("body", LANG.TOUR_ABOUT_6, "/about", "center"),
            tourStep(".tour-practice-patient", LANG.TOUR_ABOUT_7, "/about", "top"),
            tourStep("body", LANG.TOUR_ABOUT_8, "/database/examinations", "center"),
            tourStep(".tour-examinations-new", LANG.TOUR_ABOUT_NEW_EXAMINATION, "/database/examinations", "bottom"),
            tourStep(".tour-examinations-edit", LANG.TOUR_ABOUT_9, "/database/examinations", "bottom"),
            tourStep(".tour-examinations-delete", LANG.TOUR_ABOUT_10, "/database/examinations", "bottom"),
            tourStep(".tour-examinations-move", LANG.TOUR_ABOUT_11, "/database/examinations", "bottom"),
            tourStep("body", LANG.TOUR_ABOUT_12, "/database/patients", "center"),
            tourStep(".tour-patients-similar", LANG.TOUR_ABOUT_13, "/database/patients", "bottom"),
            tourStep("body", LANG.TOUR_ABOUT_14, "/database/patients", "center")
        ]
    }
};

// // // // //
// CATEGORIES
// Used in sidebar links.
// // // // //

export const DATABASE_TABLES = {
    id: "DATABASE_TABLES",
    name: "Database",
    description: "View information and perform detailed queries on various database tables.",
    path: "/database",
    icon: <StorageIcon />,
    categories: {
        examinations: {
            id: "EXAMINATIONS",
            path: "/examinations",
            name: "Examinations",
            description: "View and manage examinations.",
            visible: true,
            icon: <FeedIcon />
        },
        patients: {
            id: "PATIENTS",
            path: "/patients",
            name: "Patients",
            description: "View and manage patients.",
            visible: true,
            icon: <PermContactCalendarIcon />
        }
    }
};

export const DROPDOWN_CATEGORIES = [DATABASE_TABLES];

export const ADMINISTRATION = {
    id: "ADMINISTRATION",
    name: "Administration",
    description: "Manage administrative tasks like user accounts, roles, and permissions.",
    path: "/admin",
    icon: <EngineeringIcon />,
    categories: {
        account: {
            id: "ACCOUNT",
            path: "/account",
            name: "Account",
            description: "Manage your account",
            visible: true,
            icon: <AccountCircleIcon />
        },
        users: {
            id: "USERS",
            path: "/users",
            name: "Users",
            description: "Manage other users' accounts",
            visible: (props) => isAdmin(props),
            icon: <PeopleIcon />
        }
    }
};
